import classNames from 'classnames';

import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { Container, Box, Typography, Chip, TextField, Tooltip } from '@material-ui/core';
import { COLOR, GPT } from 'config/constants';

const useStyles = makeStyles({
  box: {
    borderRadius: 8,
    border: `1px solid ${COLOR.GREY}`,
    padding: '10px 20px',
    marginBottom: 10,
  },
  title: {
    marginBottom: 10,
  },
  chipsContent: {
    marginTop: 10,
  },
  selectedChip: {
    marginRight: 10,
    marginTop: 5,
    maxWidth: 300,
    color: COLOR.WHITE,
    '& .MuiChip-deleteIconColorSecondary': {
      color: COLOR.WHITE,
    },
  },
  chip: {
    marginRight: 10,
    marginTop: 5,
    maxWidth: 300,
  },
});
interface Props {
  title: string;
  type: string;
  oringalListingTags?: string[];
  defaultChips: string[];
  defaultSelectedChips?: string[];
  setSelectedExistingTags?: (selectedExistingTags: string[]) => void;
  setSelectedFeatureTags?: (selectedFeatureTags: string[]) => void;
  validateInputLength?: () => boolean;
  setCurrentTag?: (currentTag: string) => void;
  updateTags?: (updatedTags) => void;
}
function ChipsInput({
  title,
  type,
  oringalListingTags,
  defaultChips,
  defaultSelectedChips,
  setSelectedExistingTags,
  setSelectedFeatureTags,
  validateInputLength,
  setCurrentTag,
  updateTags,
}: Props) {
  const intl = useIntl();
  const styles = useStyles();

  const [currentInput, setCurrentInput] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [chips, setChips] = useState<string[]>(defaultChips);
  const [selectedChips, setSelectedChips] = useState<string[]>(defaultSelectedChips);
  const fmtAddFeature = intl.formatMessage({ id: 'deliver.descriptionGeneration.addFeature' });
  const fmtMaximum = intl.formatMessage({ id: 'deliver.descriptionGeneration.maximum' });
  const fmtCharacters = intl.formatMessage({ id: 'deliver.descriptionGeneration.characters' });

  const handleAddChip = (e) => {
    if (e.key === 'Enter' && !error && validateInputLength() && e.target.value.length > 0) {
      setChips((chips) => {
        return [...chips, e.target.value];
      });
      setSelectedChips((chips) => {
        return [...selectedChips, e.target.value];
      });
      if (type === 'existing') {
        setSelectedExistingTags([...selectedChips, e.target.value]);
      } else if (type === 'feature') {
        setSelectedFeatureTags([...selectedChips, e.target.value]);
      }
      setCurrentInput('');
      setCurrentTag('');
    }
  };
  const handleInputChange = (e) => {
    setCurrentInput(e.target.value);
    setCurrentTag(e.target.value);
    if (e.target.value.length >= GPT.MAX_INPUT_LENGTH) {
      setError(true);
    } else {
      setError(false);
    }
  };
  const handleClickChip = (chip: string) => {
    if (validateInputLength()) {
      setSelectedChips((selectedChips) => {
        return [...selectedChips, chip];
      });
      if (type === 'existing') {
        setSelectedExistingTags([...selectedChips, chip]);
      } else if (type === 'feature') {
        setSelectedFeatureTags([...selectedChips, chip]);
      }
    }
  };
  const handleUnselectChip = (chipToDelete: string) => {
    return () => {
      if (!defaultChips.includes(chipToDelete)) {
        setChips((chips) => {
          return chips.filter((chip) => {
            return chip !== chipToDelete;
          });
        });
      }
      setSelectedChips((selectedChips) => {
        return selectedChips.filter((chip) => {
          return chip !== chipToDelete;
        });
      });
      const newChips = selectedChips.filter((chip) => {
        return chip !== chipToDelete;
      });
      if (type === 'existing') {
        setSelectedExistingTags(newChips);
      } else if (type === 'feature') {
        setSelectedFeatureTags(newChips);
      }
      if (oringalListingTags && !oringalListingTags.includes(chipToDelete)) {
        const newCustomTags = newChips.filter((chip) => {
          return !oringalListingTags.includes(chip);
        });
        updateTags(newCustomTags);
      }
    };
  };
  return (
    <Container className={styles.box}>
      <Typography className={styles.title}>{title}</Typography>
      <TextField
        value={currentInput}
        label={fmtAddFeature}
        error={error}
        helperText={error ? fmtMaximum + GPT.MAX_INPUT_LENGTH + fmtCharacters : null}
        variant='standard'
        onChange={handleInputChange}
        onKeyDown={handleAddChip}
        fullWidth
        inputProps={{
          maxLength: GPT.MAX_INPUT_LENGTH,
        }}
      />
      <Box className={styles.chipsContent}>
        {chips.map((chip, index) => {
          return (
            <Tooltip key={index} title={chip}>
              <Chip
                className={selectedChips.includes(chip) ? styles.selectedChip : styles.chip}
                color={selectedChips.includes(chip) ? 'secondary' : 'default'}
                label={chip}
                onClick={() => {
                  return handleClickChip(chip);
                }}
                deleteIcon={
                  type === 'existing' && oringalListingTags.includes(chip) ? (
                    <RemoveCircleIcon />
                  ) : null
                }
                onDelete={selectedChips.includes(chip) ? handleUnselectChip(chip) : null}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Container>
  );
}

export default ChipsInput;
